/* istanbul ignore file */
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OktaAuthStateService } from '@okta/okta-angular';
import { Observable, catchError, concatMap, forkJoin, map, of, throwError } from 'rxjs';
import { ICategory } from '../../model/device-inventory/ICategory';
import { GoogleAnalyticsService } from './google-tag-manager.service';
import { PGHOktaService } from './pghokta.service';
import { PreInitSettingsService } from './settings/pre-init-setting.service';
import { DeviceTypeDto } from '../../model/device-inventory/device-type.dto';
import { CreateDeviceBodyDto } from '../../model/device-inventory/create-device-body.dto';
import { DeviceDto } from '../../model/device-inventory/device.dto';
import { PghUserDeviceBase } from 'src/app/shared/device/pgh-user-device';
import { InventoryInfoDto } from '../../model/device-inventory/inventory-info.dto';
import { UpdateDeviceBodyDto } from '../../model/device-inventory/update-device-body.dto';
import { buildDeviceDisplayName } from '../../shared/device/device-display-name.pipe';
import { UtilityService } from './utility.service';
import { StorageService } from './storage.service';
import { ADPResourceService } from 'digital-core';
import { InventoryWithDevices } from '../../model/device-inventory/inventory-with-devices';
import { CertificateRegisteredDevices } from 'src/app/model/account/certificate-registered-devices.dto';
import { ISelectedContract } from './settings/logged-in-user-settings';
import { ElitaPolicyItemResponseDto } from '../../model/device-inventory/elita-policy-item-response.dto';
import { formatDate } from '@angular/common';
@Injectable({
  providedIn: 'root'
})
export class DeviceInventoryService {
  private deviceInventoryMiddlewareUrl: string;
  private accessToken: string;
  private deviceInventoryGraphQlUrl: string;
  private useCaching = true;
  private middlewareUrl: string;
  private accountPath: string;
  private isEprismOnly: boolean;

  get GraphQLgetDevices(): string { return 'GraphQLgetDevices' }
  get GraphQLgetInventoryInfo(): string { return 'GraphQLgetInventoryInfo' }
  get GraphQLgetDeviceTypes(): string { return 'GraphQLgetDeviceTypes' }
  get GetEprismDeviceTypesMapping(): any[] { return [
    {code: '', deviceCode: 'SMPH', name: 'Smartphone'},
    {code: 'P', deviceCode: 'SMPH', name: 'Smartphone'},
    {code: 'W', deviceCode: 'SMAR', name: 'Smartwatches'},
    {code: 'T', deviceCode: 'TABL', name: 'Tablets'},
  ]};
  get ElitagetInventoryInfo(): string { return 'ElitagetInventoryInfo' }
  constructor(
    public httpClient: HttpClient,
    public googleAnalyticsService: GoogleAnalyticsService,
    private preInitSettingsService: PreInitSettingsService,
    public oktaAuth: OktaAuthStateService,
    private resourceService: ADPResourceService,
    private pghOktaService: PGHOktaService,
    private utilityService: UtilityService,
    private storageService: StorageService,
  ) {
    this.middlewareUrl = this.preInitSettingsService.getSettings().middleWare.middlewareUrl;
    this.accountPath = this.preInitSettingsService.getSettings().middleWare.accountPath;
    this.deviceInventoryMiddlewareUrl = this.preInitSettingsService.getSettings().middleWare.deviceInventoryMiddlewareUrl;
    this.deviceInventoryGraphQlUrl = `${this.deviceInventoryMiddlewareUrl}/graphql`;
    this.accessToken = this.pghOktaService.getAccessToken();
    const hasP360MobilePolicy = this.storageService.getLocal<string>(`EprismContractsCount`) > '0';
    const hasP360HomeTechPolicy = this.storageService.getLocal<string>(`ElitaContractsCount`) > '0';
    this.isEprismOnly = hasP360MobilePolicy && !hasP360HomeTechPolicy;
  }

  /**
   *
   * @deprecated Do not use this function for new development. This uses the
   * old API from the mobile app team. The new GraphQL APIs should be used instead.
   *
   * @returns
   */
  getDeviceInventoryCategories(useCache?: boolean): Observable<ICategory[]> {
    useCache = useCache ?? this.useCaching;

    const cacheKey = 'getDeviceInventoryCategories';
    if (useCache && sessionStorage.getItem(cacheKey)) {
      return of(JSON.parse(sessionStorage.getItem(cacheKey)));
    }

    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.accessToken}`
      }),

    };

    return this.httpClient.get<ICategory[]>(`${this.deviceInventoryMiddlewareUrl}/categories`, httpOptions)
      .pipe(
        map(res => {
          if (useCache) {
            sessionStorage.setItem(cacheKey, JSON.stringify(res));
          }
          return res;
        }),
      );
  }

  /**
   *
   * @param deviceData
   *
   * @param childCertificateNumber The id of the device inventory to add a device to
   *
   * @param catalogSource This is the source of the device information; like where it is coming from.
   * We use "USER" when the user is adding a device manually
   * We use "PTP" when the device comes from the ALDCS (Assurant Labs Device Content System)
   * We use "FING" when the device comes from this other catalog.
   * We use "ESS" when the device comes from the ESS (Equipment Search Service)
   *
   * @returns
   */
  public buildCreateDeviceBodyDtoFromPghUserDeviceBase(deviceData: PghUserDeviceBase, childCertificateNumber: string, catalogSource: 'USER' | 'PTP' | 'FING' | 'ESS'): CreateDeviceBodyDto {
    return {
      catalogSource: catalogSource,
      childCertificateNumber: childCertificateNumber,
      deviceTypeCode: deviceData.deviceTypeCode,
      make: deviceData.make,
      model: deviceData.model,
      modelIdentifier: deviceData.modelIdentifier,
      description: buildDeviceDisplayName(deviceData),
      nickName: deviceData.nickName,
      serialNumber: deviceData.serialNumber,
      purchasePrice: deviceData.purchasePrice,
      purchaseDate: deviceData.purchaseDate?.toString()
    };
  }

  public buildPghUserDeviceBaseFromDeviceDto(deviceData: DeviceDto): PghUserDeviceBase {
    if (!deviceData) {
      return null;
    }
    const retVal: PghUserDeviceBase = {
      registeredDeviceIdentifier: deviceData.registeredDeviceIdentifier,
      elitaDeviceId: deviceData.elitaDeviceId,
      deviceTypeCode: deviceData.deviceType?.code,
      deviceTypeName: deviceData.deviceType?.name,
      hasReceipt: deviceData.hasReceipt,
      receiptId: deviceData.receiptId,
      childCertificateNumber: deviceData.childCertificateNumber,
      make: deviceData.make,
      model: deviceData.model,
      modelIdentifier: deviceData.modelIdentifier,
      carrier: deviceData.carrier,
      imei: deviceData.imei,
      serialNumber: deviceData.serialNumber,
      lineNumber: deviceData.lineNumber,
      displayName: deviceData.description,
      nickName: deviceData.nickName,
      purchaseDate: deviceData.purchaseDate,
      purchasePrice: deviceData.purchasePrice,
      deviceSource: deviceData.deviceSource,
      phoneNumber: deviceData.phoneNumber,
    };

    if (deviceData?.deviceType?.coverages != null) {
      retVal.coverage = {
        hasAccidentalDamage: deviceData?.deviceType?.coverages?.hasAccidentalDamage,
        hasMechanicalDamage: deviceData?.deviceType?.coverages?.hasMechanicalDamage,
        hasLoss: deviceData?.deviceType?.coverages?.hasLoss,
        hasTheft: deviceData?.deviceType?.coverages?.hasTheft,
      };
    }

    return retVal;
  }

  private getAccessToken(): string {
    if (this.accessToken) {
      return this.accessToken;
    }
    this.accessToken = this.pghOktaService.getAccessToken();
    return this.accessToken;
  }

  /**
   * Returns the inventory info for the given policy.
   *
   * @param policyCertificateNumber
   * @param programCode Example: organic_pgh
   * @param dealerCode Example: PGHO
   */
    public getInventoryInfoGraphQL(policyCertificateNumber: string, programCode: string, dealerCode: string, useCache?: boolean): Observable<InventoryInfoDto> {
    if (this.isEprismOnly) {
      return of(undefined);
    }

    useCache = useCache ?? this.useCaching;

    const cacheKey = `${this.GraphQLgetInventoryInfo}${policyCertificateNumber}${programCode}${dealerCode}`;
    if (useCache && sessionStorage.getItem(cacheKey)) {
      return of(JSON.parse(sessionStorage.getItem(cacheKey)));
    }

    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      'Authorization': `Bearer ${this.getAccessToken()}`,
      'Accept': 'application/json'
    });

    const graphql = JSON.stringify({
      query: `
        mutation createOrGetInventory ($input: CreateInventoryInput!) {
          createOrGetInventory (input: $input) {
            id
            dealerCode,
            policyCertificateNumber,
            program {
              code,
            },
            createdAt,
            updatedAt
          }
      }`,
      variables: {
        input: {
          program: {
            code: programCode
          },
          policyCertificateNumber: policyCertificateNumber,
          dealerCode: dealerCode,
        },
      },
    });

    return this.httpClient.post<any>(this.deviceInventoryGraphQlUrl + "?inventory", graphql, { headers: headers })
      .pipe(
        map(res => {
          this.assertGraphQlResponseIsErrorFree(res, this.deviceInventoryGraphQlUrl);
          if (useCache) {
            sessionStorage.setItem(cacheKey, JSON.stringify(res?.data?.createOrGetInventory));
          }
          return res?.data?.createOrGetInventory;
        }),
      );
  }

  getElitaPolicyInfo(certificateNumber: string, dealerCode: string, useCache?: boolean): Observable<CertificateRegisteredDevices[]> {
    if (this.isEprismOnly) {
      return of(undefined);
    }
    useCache = useCache ?? this.useCaching;

    const cacheKey = `${this.ElitagetInventoryInfo}${certificateNumber}${dealerCode}`;
    if (useCache && sessionStorage.getItem(cacheKey)) {
      return of(JSON.parse(sessionStorage.getItem(cacheKey)));
    }

    const body = {
      masterCertificateNumber: certificateNumber,
      dealerCode: dealerCode
    };

    const startGetOp = performance.now();

    return this.httpClient.post<CertificateRegisteredDevices[]>(`${this.middlewareUrl}${this.accountPath}get-policy-registered-devices`, body).pipe(
      map(resp => {
        let certsArr: CertificateRegisteredDevices[] = resp;
        sessionStorage.setItem(cacheKey, JSON.stringify(certsArr));
        const endGetOp = performance.now();
        const duration = endGetOp - startGetOp;

        console.log(`Middelware get-policy-registered-devices from Elita took ${duration.toFixed(2)} milliseconds.`);
        return certsArr;
      }),
      catchError(err => {
        console.error('Error in getElitaPolicyInfo:', err);
        return throwError(err);
      })
    );
  }


  constructInventoryFromCertDetails(certificateInfoFromElita: CertificateRegisteredDevices, contract: ISelectedContract, programCode: string): InventoryWithDevices {
    let inventory: InventoryWithDevices;

    inventory = {
      devices: certificateInfoFromElita.registeredDevices,
      info: {
        id: null,
        dealerCode: contract.dealerId,
        certificateNumber: certificateInfoFromElita.certificateNumber,
        program: {
          code: programCode
        },
        deviceTypes: certificateInfoFromElita.deviceTypesCoverage,
      }
    }
    return inventory;
  }

  public getDeviceTypes(masterCertificateNumber: string, childCertificateNumber: string, dealerCode: string, typesWithCoverageOnly: boolean = false, useCache?: boolean): Observable<DeviceTypeDto[]> {
    return this.getElitaPolicyInfo(masterCertificateNumber, dealerCode, useCache).pipe(
      map(certsArr => {
        let deviceTypes = certsArr.find(cert => cert.certificateNumber === childCertificateNumber)?.deviceTypesCoverage ?? [];
        if (typesWithCoverageOnly)
          deviceTypes = deviceTypes.filter(type => type.coverages.hasAccidentalDamage || type.coverages.hasLoss || type.coverages.hasMechanicalDamage || type.coverages.hasTheft);
        return deviceTypes;
      })
    );
  }


  public getDevices(masterCertificateNumber: string, childCertificateNumber: string, dealerCode: string, useCache?: boolean): Observable<DeviceDto[]> {
    return this.getElitaPolicyInfo(masterCertificateNumber, dealerCode, useCache).pipe(
      map(certsArr => {
        return certsArr.find(cert => cert.certificateNumber === childCertificateNumber)?.registeredDevices ?? [];
      }),
      catchError(err => {
        console.error('Error in getDevices:', err);
        return throwError(err);
      })
    );
  }

  public registerDevice(device: CreateDeviceBodyDto, masterCertificateNumber: string, childCertificateNumber: string, dealerCode: string): Observable<DeviceDto> {
    const body = {
      CertificateNumber: childCertificateNumber,
      DealerCode: dealerCode,
      DeviceTypeCode: device.deviceTypeCode,
      ItemDescription: device.description,
      Manufacturer: device.make,
      Model: device.model,
      PurchasePrice: device.purchasePrice,
      PurchasedDate: device.purchaseDate,
      RegisteredItemName: device.nickName,
      RegistrationDate: formatDate(new Date(), 'yyyy-MM-dd', 'en-US'),
      SerialNumber: device.serialNumber,
      ImeiNumber: device.imei,
      CellNumber: device.lineNumber,
      Carrier: device.carrier,
      Color: device.color
    };
    return this.httpClient.post<ElitaPolicyItemResponseDto>(`${this.middlewareUrl}${this.accountPath}register-device`, body)
      .pipe(
        concatMap(registerResp => forkJoin([of(registerResp), this.getDevices(masterCertificateNumber, childCertificateNumber, dealerCode, false)])),
        map(([registerResp, certRegisteredDevices]): DeviceDto => (
          certRegisteredDevices.find(m => m?.nickName?.toLowerCase() === registerResp?.registeredItemName?.toLowerCase())
        ))
      );
  }

  updateRegisteredDevice(device: UpdateDeviceBodyDto, dealerCode: string): Observable<ElitaPolicyItemResponseDto> {

    const body = {
      certificateNumber: device.certificateNumber,
      dealerCode,
      manufacturer: device.make,
      model: device.model,
      registeredItemIdentifier: device.id,
      registeredItemName: device.nickName,
    };

    const endpoint = `${this.middlewareUrl}${this.accountPath}update-registered-device`;

    return this.httpClient.put<ElitaPolicyItemResponseDto>(endpoint, body);

  }

  deleteRegisteredDeviceById(certificateNumber: string, dealerCode: string, id: string): Observable<ElitaPolicyItemResponseDto> {

    const body = {
      certificateNumber,
      dealerCode,
      id
    };

    const endpoint = `${this.middlewareUrl}${this.accountPath}delete-registered-device-by-id`;

    return this.httpClient.delete<ElitaPolicyItemResponseDto>(endpoint, { body });

  }

  public clearCache(cacheKey: string = undefined): void {
    if (cacheKey) {
      sessionStorage.removeItem(cacheKey);
      return;
    }
    // Get all keys in sessionStorage
    const keysToDelete = Object.keys(sessionStorage);

    // Filter and keep only keys that start with 'GraphQL'
    const keysStartingWithGraphQL = keysToDelete.filter(key => key.startsWith('GraphQL'));

    // Delete the selected keys
    for (const key of keysStartingWithGraphQL) {
      sessionStorage.removeItem(key);
    }
  }

  private assertGraphQlResponseIsErrorFree(response: any, url: string) {
    // If the devices are returned then allow the user to proceed.
    if ((response?.data?.devices?.length ?? 0) > 0) {
      return;
    }
    // If there are no devices and an error exists, show the error message.
    if ((response?.errors?.length ?? 0) > 0) {
      throw new HttpErrorResponse({
        error: response,
        status: 400,
        url: url,
      });
    }
  }

    public addPhoneNumberToInventory(masterCertificateNumber: string, childCertificateNumber: string, dealerCode: string, lineNumber: string, newUser: boolean, nickname?: string, make?: string, model?: string) {
    lineNumber = this.utilityService.getPhoneNumberInE164(lineNumber);
    var device: CreateDeviceBodyDto = {
      childCertificateNumber: childCertificateNumber,
      lineNumber: lineNumber,
      description: "Invited through MyAccount",
      nickName: nickname ? nickname : "My Phone - " + Math.floor(Math.random() * 1000),
      catalogSource: "USER",
      diagnosticStatus: "PENDING",
      deviceTypeCode: "SMPH",
      make: make,
      model: model,
    };
    return this.registerDevice(device, masterCertificateNumber, childCertificateNumber, dealerCode).pipe(
      map(res => {
        if (newUser) {
          this.storageService.setSession<DeviceDto>('NewUserDevice', res);
        }
        this.clearCache();
        return res;
      })
    );
  }

  // Only Call for New Phone Device Flow
  public updateNewPhoneNumberDetails(lineNumber: string, imei: string, carrier: string, nickName: string, delearCode: string) {
    if (this.isEprismOnly) {
      return of(undefined);
    }
    let newUserPhoneDevice = this.storageService.getSession<DeviceDto>('NewUserDevice');
    if (!newUserPhoneDevice) {
      return null;
    }
    lineNumber = this.utilityService.getPhoneNumberInE164(lineNumber);
    const deviceToUpdate: UpdateDeviceBodyDto = {
      id: newUserPhoneDevice.registeredDeviceIdentifier,
      lineNumber: lineNumber,
      certificateNumber: newUserPhoneDevice.childCertificateNumber,
      carrier: carrier,
      imei: imei,
      nickName: nickName,
      deviceTypeCode: "SMPH", // PBI added to backlog to capture Device Type Code
      model: newUserPhoneDevice.model,
      make: newUserPhoneDevice.make
    };
    return this.updateRegisteredDevice(deviceToUpdate, delearCode).pipe(
      map(res => {
        this.clearCache();
        return res;
      })
    );
  }

  public getCarrierList(countryIso: string): Observable<any> {
    return this.httpClient.get<boolean>(`${this.middlewareUrl}${this.accountPath}carriers/${countryIso}`);
  }

  private replaceAmpersands(device: UpdateDeviceBodyDto | CreateDeviceBodyDto) {
    const isElita = this.resourceService.getPortalSetting('Global_CTC_IsElita').Value;
    if (!isElita || isElita === 'false') {
      return;
    }
    // If we are using Elita, we need to make sure that we do not send any &'s in a text field.
    // The SOAP endpoint will throw an error.
    if (device?.carrier?.indexOf('&') > -1) {
      device.carrier = device.carrier.replace('&', '&amp;');
    }
    if (device?.make?.indexOf('&') > -1) {
      device.make = device.make.replace('&', '&amp;');
    }
    if (device?.model?.indexOf('&') > -1) {
      device.model = device.model.replace('&', '&amp;');
    }
    if (device?.description?.indexOf('&') > -1) {
      device.description = device.description.replace('&', '&amp;');
    }
    if (device?.color?.indexOf('&') > -1) {
      device.color = device.color.replace('&', '&amp;');
    }
    if (device?.storageSize?.indexOf('&') > -1) {
      device.storageSize = device.storageSize.replace('&', '&amp;');
    }
    if (device?.nickName?.indexOf('&') > -1) {
      device.nickName = device.nickName.replace('&', '&amp;');
    }
    if (device?.serialNumber?.indexOf('&') > -1) {
      device.serialNumber = device.serialNumber.replace('&', '&amp;');
    }
  }

  updateSessionDeviceInList(masterCertificateNumber: string, dealerCode: string, updatedDevice: PghUserDeviceBase): void {

    const cacheKey = `${this.ElitagetInventoryInfo}${masterCertificateNumber}${dealerCode}`;

    const cachedData = sessionStorage.getItem(cacheKey);
    let certificatesArray: CertificateRegisteredDevices[];
    try {
      certificatesArray = JSON.parse(cachedData);
    } catch (error) {
      console.error(`Failed to parse cache data: ${error}`);
      return;
    }

    // Find certificate containing the device
    const certificate = certificatesArray.find(cert =>
      cert.certificateNumber === updatedDevice.childCertificateNumber);

    if (!certificate) {
      console.error(`Certificate not found for number: ${updatedDevice.childCertificateNumber}`);
      return;
    }

    // Find the device to update using elita ID
    const deviceToUpdate = certificate.registeredDevices.find(registeredDevice =>
      registeredDevice.elitaDeviceId === updatedDevice.elitaDeviceId);

    if (!deviceToUpdate) {
      console.error(`Device not found with ID: ${updatedDevice.elitaDeviceId}`);
      return;
    }

    deviceToUpdate.nickName = updatedDevice.nickName;
    deviceToUpdate.registeredDeviceIdentifier = updatedDevice.nickName;

    sessionStorage.setItem(cacheKey, JSON.stringify(certificatesArray));
  }


  deleteSessionDeviceFromList(masterCertificateNumber: string, dealerCode: string, deletedDevice: PghUserDeviceBase): void {

    const cacheKey = `${this.ElitagetInventoryInfo}${masterCertificateNumber}${dealerCode}`;

    const cachedData = sessionStorage.getItem(cacheKey);
    let certificatesArray: CertificateRegisteredDevices[];
    try {
      certificatesArray = JSON.parse(cachedData);
    } catch (error) {
      console.error(`Failed to parse cache data: ${error}`);
      return;
    }

    // Find certificate containing the device
    const certificate = certificatesArray.find(cert =>
      cert.certificateNumber === deletedDevice.childCertificateNumber);

    if (!certificate) {
      console.error(`Certificate not found for number: ${deletedDevice.childCertificateNumber}`);
      return;
    }

    // Find the device index to delete using elita ID
    const deviceToDeleteIndex = certificate.registeredDevices.findIndex(registeredDevice =>
      registeredDevice.elitaDeviceId === deletedDevice.elitaDeviceId);

    if (!deviceToDeleteIndex) {
      console.error(`Device not found with ID: ${deletedDevice.elitaDeviceId}`);
      return;
    }

    certificate.registeredDevices.splice(deviceToDeleteIndex, 1);

    sessionStorage.setItem(cacheKey, JSON.stringify(certificatesArray));
  }

  // TODO:Eprism - this is temprory and this is going to be removed once the new model-name field is added by eprism team
  private extractModelName(description: string): string {
    //description = 'IPhone 15 Pro 512GB Black';
    // Find the position of "GB" in the description
    const gbIndex = description.toUpperCase().indexOf('GB');
    // If "GB" is not found, return the original description
    if (gbIndex === -1) {
      return description.trim();
    }
    // Extract everything before "GB" and the storage number
    // Look backwards from GB to find where the storage size starts
    let storageStartIndex = gbIndex;
    // Move backwards to find the start of the storage number
    while (storageStartIndex > 0 && /[\d\s]/.test(description[storageStartIndex - 1])) {
      storageStartIndex--;
    }
    // Extract the model name (everything before the storage size)
    const modelName = description.substring(0, storageStartIndex).trim();
    return modelName;
  }
}
